import React, { useEffect, useState } from 'react'
import {
  List,
  Datagrid,
  TextField,
  TextInput,
  useTranslate,
  useResourceContext,
  useResourceDefinitions,
  useListController,
  useStore,
  DateField,
  FunctionField,
  BooleanInput,
  DateTimeInput
} from 'react-admin'
import { Container, Skeleton } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { ListViewActionsButtons } from 'src/components/uiElements/buttons/ListViewActionsButtons'
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import { hasPermissionFunction } from 'src/helper/HasPermission'
import { InstallmentExportButton } from './logic/InstallmentExportButon'
import { PERMISSION_EXPORT, PERMISSION_IMPORT } from 'src/helper/Permission'
import { workUnitPaymentServices } from 'src/services/payment/workUnit/WorkUnit.service'
import { AutocompleteInput } from 'src/components/uiElements/fields/AutocompleteInput'
import { companyServices } from 'src/services/payment/company/company.service'
import { gatewayServices } from 'src/services/payment/gateway/gateway.service'
import { financialEntityServices } from 'src/services/payment/financialEntity/financialEntity.service'
import orderBy from 'lodash/orderBy'
import { cardServices } from 'src/services/payment/card/card.service'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'
import { InstallmentImportButton } from './logic/InstallmentImportButon'

const ProductTypeInput = ({ label, source }) => {
  const [choices, setChoices] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getProductType()
  }, [])

  const getProductType = async () => {
    try {
      setLoading(true)
      const response = await productTypeServices.getByParam()
      setChoices(response)
      setLoading(false)
    } catch (error) {
      setChoices([])
      setLoading(false)
    }
  }

  return (
    loading ? (
      <Skeleton variant='rectangular' height={40} width='300px' />
    ) : (
      <AutocompleteInput
        source={source}
        label={label}
        showLabel
        options={choices}
        optionsText={['long_name']}
        optionValue='id'
        resetOnLoad={false}
        style={{ width: '300px' }}
        disableClearable
        useOptionValue
        useOptionValueByOptions
      />
    )
  )
}

const WorkUnitInput = ({ label, source }) => {
  const [choices, setChoices] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getWorkUnit()
  }, [])

  const getWorkUnit = async () => {
    try {
      setLoading(true)
      const allWorkUnit = await workUnitPaymentServices.getByAll()
      setChoices(allWorkUnit)
      setLoading(false)
    } catch (error) {
      setChoices([])
      setLoading(false)
    }
  }

  return (
    loading ? (
      <Skeleton variant='rectangular' height={40} width='300px' />
    ) : (
      <AutocompleteInput
        source={source}
        label={label}
        showLabel
        options={choices}
        optionsText={['long_name']}
        optionValue='id'
        resetOnLoad={false}
        style={{ width: '300px' }}
        disableClearable
        useOptionValue
        useOptionValueByOptions
      />
    )
  )
}

const CompanyInput = ({ label, source }) => {
  const [choices, setChoices] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getCompany()
  }, [])

  const getCompany = async () => {
    try {
      setLoading(true)
      const companies = await companyServices.getByAll({ limit: 999 })
      setChoices(companies)
    } catch (error) {
      setChoices([])
    } finally {
      setLoading(false)
    }
  }

  return (
    loading ? (
      <Skeleton variant='rectangular' height={40} width='300px' />
    ) : (
      <AutocompleteInput
        source={source}
        label={label}
        showLabel
        options={choices}
        optionsText={['short_name', 'long_name']}
        optionValue='id'
        resetOnLoad={false}
        style={{ width: '300px' }}
        disableClearable
        useOptionValue
        useOptionValueByOptions
      />
    )
  )
}

const GatewayInput = ({ label, source }) => {
  const [choices, setChoices] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getGateway()
  }, [])

  const getGateway = async () => {
    try {
      setLoading(true)
      const response = await gatewayServices.getByAll({ limit: 999 })
      setChoices(response)
      setLoading(false)
    } catch (error) {
      setChoices([])
      setLoading(false)
    }
  }

  return (
    loading ? (
      <Skeleton variant='rectangular' height={40} width='200px' />
    ) : (
      <AutocompleteInput
        source={source}
        label={label}
        showLabel
        options={choices}
        optionsText={['name']}
        optionValue='id'
        resetOnLoad={false}
        style={{ width: '200px' }}
        disableClearable
        useOptionValue
        useOptionValueByOptions
      />
    )
  )
}

const CardInput = ({ label, source }) => {
  const [choices, setChoices] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getCard()
  }, [])

  const getCard = async () => {
    try {
      setLoading(true)
      const response = await cardServices.getByAll()
      setChoices(orderBy(response, ['long_name'], ['asc']))
      setLoading(false)
    } catch (error) {
      setChoices([])
      setLoading(false)
    }
  }

  return (
    loading ? (
      <Skeleton variant='rectangular' height={40} width='300px' />
    ) : (
      <AutocompleteInput
        source={source}
        label={label}
        showLabel
        options={choices}
        optionsText={['long_name']}
        optionValue='id'
        resetOnLoad={false}
        style={{ width: '300px' }}
        disableClearable
        useOptionValue
        useOptionValueByOptions
      />
    )
  )
}

const FinancialEntityInput = ({ label, source }) => {
  const [choices, setChoices] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getFinancialEntity()
  }, [])

  const getFinancialEntity = async () => {
    try {
      setLoading(true)
      const response = await financialEntityServices.getByAll()
      setChoices(orderBy(response, ['long_name'], ['asc']))
      setLoading(false)
    } catch (error) {
      setChoices([])
      setLoading(false)
    }
  }

  return (
    loading ? (
      <Skeleton variant='rectangular' height={40} width='300px' />
    ) : (
      <AutocompleteInput
        source={source}
        label={label}
        showLabel
        options={choices}
        optionsText={['long_name']}
        optionValue='id'
        resetOnLoad={false}
        style={{ width: '300px' }}
        disableClearable
        useOptionValue
        useOptionValueByOptions
      />
    )
  )
}

export const InstallmentList = (props) => {
  const translate = useTranslate()
  const { setSort, isFetching } = useListController()
  const [permissionsStore, setPermissionsStore] = useStore('currentResource', '{}')
  const [permissions, setPermissions] = useState(null)
  const [isSorting, setIsSorting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const propsResource = useResourceDefinitions()
  const resourceName = useResourceContext()

  useEffect(() => {
    setPermissionsStore(JSON.stringify(propsResource[resourceName]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsResource, resourceName])

  useEffect(() => {
    if (permissionsStore) {
      setPermissions(JSON.parse(permissionsStore))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsStore])

  useEffect(() => {
    if (isSorting && isFetching) {
      setIsLoading(true)
    } else if (isLoading && isSorting && !isFetching) {
      setIsLoading(false)
      setIsSorting(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSorting, isFetching])

  const onClickSort = (sort) => {
    setSort(sort)
    setIsSorting(true)
  }

  const convertToUTC = (dateString) => dateString

  const convertFromUTC = (dateString) => dateString

  const installmentFilters = [
    <TextInput label={translate('ra.field.id')} source='id' defaultValue='' />,
    <ProductTypeInput label={translate('ra.field.product_type')} source='product_id' defaultValue='' />,
    <WorkUnitInput label={translate('ra.field.work_unit')} source='work_unit_id' />,
    <CompanyInput label={translate('ra.field.company')} source='company_id' defaultValue='' />,
    <GatewayInput label={translate('ra.field.gateway')} source='gateway_id' defaultValue='' />,
    <CardInput label={translate('ra.field.card')} source='card_id' defaultValue='' />,
    <FinancialEntityInput label={translate('ra.field.bank')} source='financial_entity_id' defaultValue='' />,
    <TextInput label={translate('ra.field.installments')} source='quota' defaultValue='' />,
    <BooleanInput label={translate('ra.field.is_active')} source='is_active' />,
    <DateTimeInput
      label={translate('ra.field.start_validity')}
      source='date_start__gte'
      format={convertFromUTC}
      parse={convertToUTC}
    />,
    <DateTimeInput
      label={translate('ra.field.end_validity')}
      source='date_validity__lte'
      format={convertFromUTC}
      parse={convertToUTC}
    />,
    <DateTimeInput
      label={translate('ra.field.last_update')}
      source='updated_at__gte'
      format={convertFromUTC}
      parse={convertToUTC}
    />
  ]

  const hasPermissions = () => {
    return permissions?.options?.revision ||
      permissions?.hasShow ||
      permissions?.hasEdit ||
      permissions?.options?.delete
  }

  return (
    <Container className='content'>
      <CardCommon title={translate('ra.entity.installment')}>
        <List
          {...props}
          component='div'
          filters={installmentFilters}
          actions={
            <ListViewTopToolbar
              hasCreate={permissions?.hasCreate}
              exporterDisable={true}
              CustomExportButton={
                hasPermissionFunction('installment', PERMISSION_EXPORT) ? InstallmentExportButton : null
              }
              CustomImportButton={
                hasPermissionFunction('installment', PERMISSION_IMPORT) ? InstallmentImportButton : null
              }
            />
          }
          exporter={false}
        >
          <Datagrid rowClick='show' setSort={onClickSort} isLoading={isLoading}>
            <TextField source='id' label={translate('ra.field.id')} />
            <TextField source='product_name' label={translate('ra.field.product_type')} />
            <TextField source='work_unit_name' label={translate('ra.field.work_unit')} />
            <TextField source='company_relation.long_name' label={translate('ra.field.company')} />
            <TextField source='gateway_relation.name' label={translate('ra.field.gateway')} />
            <TextField source='card_relation.long_name' label={translate('ra.field.card')} />
            <FunctionField
              label={translate('ra.field.type')}
              render={(record) => !record.type || translate(`ra.card_type.${record.type.toLowerCase()}`).includes('.')
                ? record.type
                : translate(`ra.card_type.${record.type.toLowerCase()}`).toUpperCase()
              }
            />
            <TextField source='financial_entity_relation.long_name' label={translate('ra.field.bank')} />
            <TextField source='quota' label={translate('ra.field.installments')} />
            <DateField source='date_start' label={translate('ra.field.start_validity')} />
            <DateField source='date_validity' label={translate('ra.field.end_validity')} />
            <DateField source='updated_at' label={translate('ra.field.last_update')} />
            <TextField source='coef' label={translate('ra.field.coef')} />
            {hasPermissions() && (
              <ListViewActionsButtons label={translate('ra.action.actions')} fieldsName={['short_name']} genderElement='femenine' clone={true} />
            )}
          </Datagrid>
        </List>
      </CardCommon>
    </Container>
  )
}
